import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { ArrowBack, Download, Refresh, Delete, Lock, FilterList, Clear } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { analyticsTracker, AnalyticsStats, VisitData } from './utils/AnalyticsTracker';

// Password for accessing stats page - Change this to your desired password
const STATS_PASSWORD = 'admin2024';

const AnalyticsStatsPage: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<AnalyticsStats | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [passwordError, setPasswordError] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');

    const loadStats = () => {
        try {
            const loadedStats = analyticsTracker.getStats();
            setStats(loadedStats);
            setError(null);
        } catch (err) {
            console.error('Error loading stats:', err);
            setError(err instanceof Error ? err.message : 'Failed to load statistics');
            // Set empty stats as fallback
            setStats({
                totalVisits: 0,
                uniqueVisitors: 0,
                visits: [],
                visitsByDate: {},
                visitsByPath: {},
                visitsByCountry: {},
                visitsByIP: {},
                recentVisits: [],
                firstVisit: null,
                lastVisit: null,
            });
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            loadStats();
        }
    }, [isAuthenticated]);

    const handlePasswordSubmit = () => {
        if (password === STATS_PASSWORD) {
            setIsAuthenticated(true);
            setPasswordError('');
            loadStats();
        } else {
            setPasswordError('Incorrect password. Please try again.');
            setPassword('');
        }
    };

    const handlePasswordCancel = () => {
        navigate('/');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handlePasswordSubmit();
        }
    };

    const handleBack = () => {
        navigate('/');
    };

    const handleExport = () => {
        const data = analyticsTracker.exportStats();
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        if (window.confirm('Are you sure you want to clear all analytics data? This cannot be undone.')) {
            analyticsTracker.clearStats();
            loadStats();
        }
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleString();
    };

    const formatDateShort = (dateKey: string) => {
        return new Date(dateKey).toLocaleDateString();
    };

    // Filter visits by date range
    const filteredVisits = useMemo(() => {
        if (!stats || !stats.visits) return [];
        
        let filtered = stats.visits;
        
        if (dateFrom) {
            const fromDate = new Date(dateFrom).setHours(0, 0, 0, 0);
            filtered = filtered.filter(v => v.timestamp >= fromDate);
        }
        
        if (dateTo) {
            const toDate = new Date(dateTo).setHours(23, 59, 59, 999);
            filtered = filtered.filter(v => v.timestamp <= toDate);
        }
        
        return filtered;
    }, [stats, dateFrom, dateTo]);

    // Compute filtered statistics
    const filteredStats = useMemo(() => {
        if (!stats || filteredVisits.length === 0) {
            return {
                totalVisits: 0,
                uniqueVisitors: 0,
                visitsByDate: {},
                visitsByPath: {},
                visitsByCountry: {},
                visitsByIP: {},
                recentVisits: [],
            };
        }

        const visitsByDate: Record<string, number> = {};
        const visitsByPath: Record<string, number> = {};
        const visitsByCountry: Record<string, number> = {};
        const visitsByIP: Record<string, number> = {};
        const uniqueVisitorsSet = new Set<string>();

        filteredVisits.forEach(visit => {
            // Count by date
            const dateKey = new Date(visit.timestamp).toISOString().split('T')[0];
            visitsByDate[dateKey] = (visitsByDate[dateKey] || 0) + 1;

            // Count by path
            visitsByPath[visit.path] = (visitsByPath[visit.path] || 0) + 1;

            // Count by country
            if (visit.country && visit.country !== 'Unknown') {
                visitsByCountry[visit.country] = (visitsByCountry[visit.country] || 0) + 1;
            }

            // Count by IP
            if (visit.ip && visit.ip !== 'Unknown') {
                visitsByIP[visit.ip] = (visitsByIP[visit.ip] || 0) + 1;
            }

            // Track unique visitors
            if (visit.sessionId) {
                uniqueVisitorsSet.add(visit.sessionId);
            }
        });

        return {
            totalVisits: filteredVisits.length,
            uniqueVisitors: uniqueVisitorsSet.size,
            visitsByDate,
            visitsByPath,
            visitsByCountry,
            visitsByIP,
            recentVisits: filteredVisits.slice(-50).reverse(),
        };
    }, [filteredVisits]);

    const getTopPaths = () => {
        if (!filteredStats) return [];
        return Object.entries(filteredStats.visitsByPath || {})
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10);
    };

    const getDailyStats = () => {
        if (!filteredStats) return [];
        return Object.entries(filteredStats.visitsByDate || {})
            .sort(([a], [b]) => a.localeCompare(b));
    };

    const getTopCountries = () => {
        if (!filteredStats || !filteredStats.visitsByCountry) return [];
        return Object.entries(filteredStats.visitsByCountry)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10);
    };

    const getTopIPs = () => {
        if (!filteredStats || !filteredStats.visitsByIP) return [];
        return Object.entries(filteredStats.visitsByIP)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10);
    };

    const handleClearFilter = () => {
        setDateFrom('');
        setDateTo('');
    };

    // Show password dialog if not authenticated
    if (!isAuthenticated) {
        return (
            <Dialog 
                open={true} 
                onClose={handlePasswordCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(135deg, rgb(10, 12, 22) 0%, rgb(44, 26, 61) 100%)',
                        border: '2px solid #00E5FF',
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle sx={{ color: '#CCF7FF', textAlign: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Lock />
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Protected Access
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <Typography variant="body2" sx={{ color: '#DFFBFF', mb: 2, textAlign: 'center' }}>
                        This page is password protected. Please enter the access code to continue.
                    </Typography>
                    <TextField
                        fullWidth
                        type="password"
                        label="Access Code"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setPasswordError('');
                        }}
                        onKeyPress={handleKeyPress}
                        error={!!passwordError}
                        helperText={passwordError}
                        autoFocus
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#DFFBFF',
                                '& fieldset': {
                                    borderColor: '#00E5FF',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#00F0FF',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#00E5FF',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#9EDCE8',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#00E5FF',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 2 }}>
                    <Button 
                        onClick={handlePasswordCancel}
                        variant="outlined"
                        sx={{
                            color: '#CCF7FF',
                            borderColor: '#00E5FF',
                            '&:hover': {
                                borderColor: '#00F0FF',
                                background: 'rgba(0,229,255,0.1)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handlePasswordSubmit}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(90deg, #00E5FF 0%, #00B8D4 100%)',
                            color: '#001018',
                            fontWeight: 800,
                            boxShadow: '0 0 12px rgba(0,229,255,0.4)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #00F0FF 0%, #00CBE4 100%)',
                                boxShadow: '0 0 16px rgba(0,229,255,0.6)',
                            },
                        }}
                    >
                        Access
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    if (!stats) {
        return (
            <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, rgb(10, 12, 22) 0%, rgb(44, 26, 61) 100%)', p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white' }}>Loading statistics...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, rgb(10, 12, 22) 0%, rgb(44, 26, 61) 100%)', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'red' }}>Error: {error}</Typography>
                <Button onClick={loadStats} variant="contained">Retry</Button>
                <Button onClick={handleBack} variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>Go Back</Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, rgb(10, 12, 22) 0%, rgb(44, 26, 61) 100%)',
                display: 'flex',
                flexDirection: 'column',
                pb: 4,
                height: '100vh',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            {/* Header */}
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Button
                    variant="contained"
                    startIcon={<ArrowBack />}
                    onClick={handleBack}
                    sx={{
                        mb: 2,
                        background: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                            background: 'rgba(255,255,255,0.3)',
                        },
                    }}
                >
                    Back to Game Launcher
                </Button>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        mb: 2,
                    }}
                >
                    Visitor Statistics
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<Refresh />}
                        onClick={loadStats}
                        sx={{
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                background: 'rgba(255,255,255,0.3)',
                            },
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleExport}
                        sx={{
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                background: 'rgba(255,255,255,0.3)',
                            },
                        }}
                    >
                        Export JSON
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Delete />}
                        onClick={handleClear}
                        sx={{
                            background: 'rgba(255,0,0,0.3)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                background: 'rgba(255,0,0,0.4)',
                            },
                        }}
                    >
                        Clear Data
                    </Button>
                    
                    {/* Date Filter - Inline */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexWrap: 'wrap' }}>
                        <FilterList sx={{ color: '#00E5FF', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#DFFBFF', fontWeight: 600, mr: 1 }}>
                            Filter:
                        </Typography>
                        <TextField
                            type="date"
                            label="From"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            sx={{
                                minWidth: 150,
                                '& .MuiOutlinedInput-root': {
                                    color: '#DFFBFF',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    '& fieldset': {
                                        borderColor: '#00E5FF',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#00F0FF',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#00E5FF',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#9EDCE8',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#00E5FF',
                                },
                            }}
                        />
                        <TextField
                            type="date"
                            label="To"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            sx={{
                                minWidth: 150,
                                '& .MuiOutlinedInput-root': {
                                    color: '#DFFBFF',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    '& fieldset': {
                                        borderColor: '#00E5FF',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#00F0FF',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#00E5FF',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#9EDCE8',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#00E5FF',
                                },
                            }}
                        />
                        {(dateFrom || dateTo) && (
                            <Button
                                variant="outlined"
                                startIcon={<Clear />}
                                onClick={handleClearFilter}
                                size="small"
                                sx={{
                                    color: '#CCF7FF',
                                    borderColor: '#00E5FF',
                                    '&:hover': {
                                        borderColor: '#00F0FF',
                                        background: 'rgba(0,229,255,0.1)',
                                    },
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </Box>
                </Box>
                {(dateFrom || dateTo) && (
                    <Typography variant="caption" sx={{ color: '#9EDCE8', display: 'block', textAlign: 'center', mb: 2 }}>
                        Showing {filteredStats.totalVisits} visits in selected date range
                    </Typography>
                )}
            </Box>

            {/* Content */}
            <Container maxWidth="xl" sx={{ flex: 1, py: 2, pb: 4 }}>
                {stats.totalVisits === 0 ? (
                    <Paper sx={{ p: 4, background: 'rgba(255,255,255,0.95)', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
                            No Visit Data Yet
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                            Statistics will appear here once visitors start accessing your site.
                        </Typography>
                        <Button onClick={handleBack} variant="contained">
                            Back to Home
                        </Button>
                    </Paper>
                ) : (
                <Grid container spacing={3}>
                    {/* Summary Cards */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            sx={{
                                p: 3,
                                background: 'rgba(255,255,255,0.95)',
                                borderRadius: 2,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                                {filteredStats?.totalVisits ?? stats?.totalVisits ?? 0}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                Total Visits {(dateFrom || dateTo) && '(Filtered)'}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            sx={{
                                p: 3,
                                background: 'rgba(255,255,255,0.95)',
                                borderRadius: 2,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#764ba2' }}>
                                {filteredStats?.uniqueVisitors ?? stats?.uniqueVisitors ?? 0}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                Unique Visitors {(dateFrom || dateTo) && '(Filtered)'}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            sx={{
                                p: 3,
                                background: 'rgba(255,255,255,0.95)',
                                borderRadius: 2,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f093fb' }}>
                                {stats.firstVisit ? formatDate(stats.firstVisit).split(',')[0] : 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                First Visit
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            sx={{
                                p: 3,
                                background: 'rgba(255,255,255,0.95)',
                                borderRadius: 2,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4facfe' }}>
                                {stats.lastVisit ? formatDate(stats.lastVisit).split(',')[0] : 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                Last Visit
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* Top Pages */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: 'rgba(255,255,255,0.95)', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Top Pages
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Path</TableCell>
                                            <TableCell align="right">Visits</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getTopPaths().map(([path, count]) => (
                                            <TableRow key={path}>
                                                <TableCell>{path || '/'}</TableCell>
                                                <TableCell align="right">
                                                    <Chip label={count} size="small" color="primary" />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Top Countries */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: 'rgba(255,255,255,0.95)', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Top Countries
                            </Typography>
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Country</TableCell>
                                            <TableCell align="right">Visits</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getTopCountries().map(([country, count]) => (
                                            <TableRow key={country}>
                                                <TableCell>{country}</TableCell>
                                                <TableCell align="right">
                                                    <Chip label={count} size="small" color="primary" />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Daily Statistics */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: 'rgba(255,255,255,0.95)', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Daily Visits (Last 30 Days)
                            </Typography>
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">Visits</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getDailyStats().map(([date, count]) => (
                                            <TableRow key={date}>
                                                <TableCell>{formatDateShort(date)}</TableCell>
                                                <TableCell align="right">
                                                    <Chip label={count} size="small" color="secondary" />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Top IPs */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: 'rgba(255,255,255,0.95)', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Top IP Addresses
                            </Typography>
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>IP Address</TableCell>
                                            <TableCell align="right">Visits</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getTopIPs().map(([ip, count]) => (
                                            <TableRow key={ip}>
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                        {ip}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip label={count} size="small" color="warning" />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Recent Visits */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, background: 'rgba(255,255,255,0.95)', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Recent Visits (Last 50)
                            </Typography>
                            <TableContainer sx={{ maxHeight: 500 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Timestamp</TableCell>
                                            <TableCell>Path</TableCell>
                                            <TableCell>IP Address</TableCell>
                                            <TableCell>Country</TableCell>
                                            <TableCell>City</TableCell>
                                            <TableCell>Referrer</TableCell>
                                            <TableCell>Device</TableCell>
                                            <TableCell>Screen</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(filteredStats?.recentVisits || stats?.recentVisits || []).map((visit, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{formatDate(visit.timestamp)}</TableCell>
                                                <TableCell>
                                                    <Chip label={visit.path || '/'} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                        {visit.ip || 'Unknown'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {visit.country && visit.country !== 'Unknown' ? (
                                                        <Chip 
                                                            label={`${visit.countryCode || ''} ${visit.country}`} 
                                                            size="small" 
                                                            color="success" 
                                                        />
                                                    ) : (
                                                        <Typography variant="caption">Unknown</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {visit.city || 'Unknown'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {visit.referrer === 'direct' ? (
                                                        <Chip label="Direct" size="small" color="info" />
                                                    ) : (
                                                        <Typography variant="caption" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {visit.referrer}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                        {visit.userAgent.split(' ')[0]}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{visit.screenWidth}x{visit.screenHeight}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
                )}
            </Container>
        </Box>
    );
};

export default AnalyticsStatsPage;
